@if (moduleId()) {
  <button
    type="button"
    class="med__overlay"
    [attr.aria-label]="'modules.drawer.close' | translate"
    (click)="onOverlayClick()"
  ></button>
  <div
    class="med"
    role="dialog"
    aria-modal="true"
    data-testid="module-drawer"
    [attr.aria-label]="'modules.drawer.ariaLabel' | translate"
    cdkTrapFocus
    [cdkTrapFocusAutoCapture]="true"
    (keydown)="onKeydown($event)"
  >
    <header class="med__header">
      <div class="med__heading">
        @if (canGoBack()) {
          <button
            type="button"
            class="med__back"
            [attr.aria-label]="'modules.drawer.back' | translate"
            (click)="back()"
          >
            <mat-icon aria-hidden="true">arrow_back</mat-icon>
          </button>
        }
        <span class="med__badge" aria-hidden="true"><mat-icon>extension</mat-icon></span>
        <div class="med__heading-text">
          <div class="med__eyebrow">
            {{ 'modules.drawer.eyebrow' | translate }}
            @if (meta()?.key) {
              · {{ meta()?.key }}
            }
          </div>
          <div class="med__title-line">{{ title() || ('modules.noTitle' | translate) }}</div>
        </div>
      </div>
      <button
        type="button"
        class="med__close"
        data-testid="module-drawer-close"
        [attr.aria-label]="'modules.drawer.close' | translate"
        (click)="requestClose()"
      >
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </header>

    <div class="med__body">
      @if (loading()) {
        <div class="med__loading"><mat-spinner diameter="28" /></div>
      } @else {
        @if (others().length > 0) {
          <div class="med__presence" aria-live="polite">
            <span class="med__presence-dot" aria-hidden="true"></span>
            <span class="med__presence-avatars">
              @for (user of others(); track user.tenantUserId) {
                <app-avatar [name]="user.displayName" [size]="22" />
              }
            </span>
            <span class="med__presence-text">
              {{ 'modules.drawer.presence' | translate: { names: presenceNames() } }}
            </span>
          </div>
        }
        <label class="med__label" for="med-title">{{ 'modules.drawer.moduleTitle' | translate }}</label>
        <input
          #titleInput
          id="med-title"
          class="med__title-input"
          type="text"
          [value]="title()"
          [attr.maxlength]="fieldLengths.NAME_MAX"
          (input)="onTitleInput($event)"
          [placeholder]="'modules.drawer.moduleTitlePlaceholder' | translate"
        />

        <label class="med__label" for="med-desc">{{ 'modules.drawer.description' | translate }}</label>
        <textarea
          id="med-desc"
          class="med__desc-input"
          rows="3"
          [value]="description()"
          [attr.maxlength]="fieldLengths.FREETEXT_MAX"
          (input)="onDescriptionInput($event)"
          [placeholder]="'modules.drawer.descriptionPlaceholder' | translate"
        ></textarea>

        <app-person-select
          class="med__author"
          [formControl]="authorControl"
          [label]="'modules.drawer.author' | translate"
          [options]="personOptions()"
          [allowNone]="false"
          [required]="true"
        ></app-person-select>
        @if (authorControl.hasError('required') && authorControl.touched) {
          <p class="med__field-error" role="alert">{{ 'modules.drawer.authorRequired' | translate }}</p>
        }

        <section class="med__tdb-linking" data-testid="module-drawer-tdb-linking">
          <div class="med__tdb-linking-head">
            <span class="med__label med__label--inline">{{ 'modules.drawer.tdbLinking.title' | translate }}</span>
            <span class="med__tdb-linking-count" aria-hidden="true">{{ drawerLinkedSheets().length }}</span>
            <span class="sr-only" aria-live="polite">
              {{ linkedCountKey() | translate: { count: drawerLinkedSheets().length } }}
            </span>
          </div>
          @if (drawerLinkedSheets().length === 0) {
            <p class="med__tdb-linking-empty">{{ 'modules.drawer.tdbLinking.empty' | translate }}</p>
          } @else if (tdbEditingEnabled()) {
            <app-testdaten-block
              [moduleTitle]="title()"
              [rows]="topBlockRows()"
              [titleKey]="topBlockTitleKey()"
              [disabled]="readOnly()"
              [unlinkable]="!readOnly()"
              (overrideChanged)="onTopBlockChanged($event)"
              (overrideReset)="onTopBlockReset($event)"
              (unlinkRequested)="onTopBlockUnlink($event)"
            />
          } @else {
            <ul class="med__tdb-linking-list" role="list">
              @for (sheet of drawerLinkedSheets(); track sheet.id) {
                <li class="med__tdb-linking-item" [attr.data-testid]="'module-drawer-tdb-row-' + sheet.key">
                  <div class="med__tdb-linking-item-head">
                    <mat-icon aria-hidden="true">table_chart</mat-icon>
                    <code class="med__tdb-linking-id">{{ sheet.key }}</code>
                    <span class="med__tdb-linking-name">{{ sheet.name }}</span>
                    <span class="med__tdb-linking-rows">
                      {{ 'tdb.status.rowsAll' | translate: { count: sheet.zeilen.length } }}
                    </span>
                    <button
                      type="button"
                      class="med__tdb-linking-unlink"
                      [attr.aria-label]="'modules.drawer.tdbLinking.unlink' | translate: { key: sheet.key }"
                      (click)="unlinkSheet(sheet)"
                    >
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  </div>
                </li>
              }
            </ul>
          }
          <div class="med__tdb-linking-combobox" (focusout)="onTdbLinkFocusOut($event)">
            <input
              class="med__tdb-linking-search"
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="med-tdb-linking-options"
              [attr.aria-expanded]="tdbLinkOpen()"
              [value]="tdbLinkSearch()"
              [attr.aria-label]="'modules.drawer.tdbLinking.searchAria' | translate"
              [placeholder]="'modules.drawer.tdbLinking.searchPlaceholder' | translate"
              (input)="onTdbLinkSearch($event)"
              (focus)="tdbLinkOpen.set(true)"
              (click)="tdbLinkOpen.set(true)"
            />
            @if (tdbLinkOpen() && drawerLinkOptions().length > 0) {
              <ul class="med__tdb-linking-options" id="med-tdb-linking-options" role="listbox">
                @for (option of drawerLinkOptions(); track option.id) {
                  <li role="presentation">
                    <button
                      type="button"
                      class="med__tdb-linking-option"
                      role="option"
                      [attr.aria-selected]="isSheetLinkedToActiveModule(option)"
                      [disabled]="isSheetLinkedToActiveModule(option)"
                      (mousedown)="$event.preventDefault()"
                      (click)="linkSheet(option)"
                    >
                      <code>{{ option.key }}</code>
                      <span>{{ option.name }}</span>
                      <span class="med__tdb-linking-option-rows">
                        {{ 'modules.drawer.tdbLinking.rowCount' | translate: { count: option.zeilen.length } }}
                      </span>
                    </button>
                  </li>
                }
              </ul>
            } @else if (tdbLinkOpen() && tdbLinkSearch() && drawerLinkOptions().length === 0) {
              <p class="med__tdb-linking-empty-search">
                {{ 'modules.drawer.tdbLinking.noMatch' | translate }}
              </p>
            }
          </div>
        </section>

        <div class="med__ablauf-head">
          <span class="med__label med__label--inline">{{ 'modules.drawer.ablauf' | translate }}</span>
          <span class="med__count">{{ steps().length }}</span>
        </div>

        <div class="med__items">
          @for (item of steps(); track item.id; let i = $index) {
            <div class="med__item">
              <span class="med__item-nr" aria-hidden="true">{{ i + 1 }}</span>
              @if (isModuleItem(item)) {
                <div class="med__module-block">
                  <button
                    type="button"
                    class="med__module-chip med__module-chip--with-tdb"
                    [attr.aria-label]="'modules.drawer.openSubModule' | translate: { name: moduleItemName(item) }"
                    (click)="openSubModule(item)"
                  >
                    <mat-icon aria-hidden="true">extension</mat-icon>
                    <span class="med__module-chip-name">{{ moduleItemName(item) }}</span>
                    <span class="med__module-chip-tag">{{ 'modules.drawer.moduleTag' | translate }}</span>
                  </button>
                  <div class="med__module-tdbs" [attr.data-testid]="'module-drawer-nested-tdb-' + item.id">
                    <div class="med__module-tdbs-head">
                      <span class="med__module-tdbs-title">
                        {{ 'modules.drawer.tdbLinking.title' | translate }}
                      </span>
                      <span class="med__module-tdbs-count">
                        {{ moduleStepLinkedSheets(item).length }}
                      </span>
                    </div>
                    @if (moduleStepLinkedSheets(item).length === 0) {
                      <p class="med__module-tdbs-empty">
                        {{ 'modules.drawer.tdbLinking.empty' | translate }}
                      </p>
                    }
                    @if (!moduleStepHasActiveSheet(item)) {
                      <div
                        class="med__tdb-linking-editor med__module-tdb-editor"
                        [attr.data-testid]="'module-drawer-nested-condition-' + item.id"
                      >
                        <div class="med__tdb-linking-field">
                          <div class="med__tdb-linking-field-label">
                            {{ 'modules.drawer.tdbLinking.condition' | translate }}
                          </div>
                          <app-step-condition-editor
                            [override]="moduleStepDraft(item)"
                            [scopeId]="moduleItemName(item)"
                            [disabled]="readOnly()"
                            (changed)="onModuleStepConditionChange(item, $event)"
                          />
                        </div>
                        <code
                          class="med__tdb-linking-preview"
                          [attr.data-testid]="'module-drawer-nested-condition-preview-' + item.id"
                        >
                          {{ moduleStepConditionPreview(item) }}
                        </code>
                      </div>
                    }
                    @if (moduleStepLinkedSheets(item).length) {
                      <app-testdaten-block
                        [moduleTitle]="moduleItemName(item)"
                        [rows]="moduleStepBlockRows(item)"
                        titleKey="tdb.title.module"
                        [disabled]="readOnly()"
                        [unlinkable]="!readOnly()"
                        (overrideChanged)="onModuleStepBlockChanged(item, $event)"
                        (overrideReset)="onModuleStepBlockReset(item, $event)"
                        (unlinkRequested)="onModuleStepBlockUnlink(item, $event)"
                      />
                    }
                    <div class="med__module-tdbs-combobox" (focusout)="onModuleStepLinkFocusOut(item, $event)">
                      <input
                        class="med__tdb-linking-search"
                        type="text"
                        role="combobox"
                        aria-autocomplete="list"
                        [attr.aria-controls]="'med-module-tdb-options-' + item.id"
                        [attr.aria-expanded]="moduleStepLinkOpenFor(item)"
                        [value]="moduleStepLinkSearchFor(item)"
                        [attr.aria-label]="'modules.drawer.tdbLinking.searchAria' | translate"
                        [placeholder]="'modules.drawer.tdbLinking.searchPlaceholder' | translate"
                        (input)="onModuleStepLinkSearch(item, $event)"
                        (focus)="onModuleStepLinkFocus(item)"
                        (click)="onModuleStepLinkFocus(item)"
                      />
                      @if (moduleStepLinkOpenFor(item) && moduleStepLinkOptions(item).length > 0) {
                        <ul class="med__tdb-linking-options" [id]="'med-module-tdb-options-' + item.id" role="listbox">
                          @for (option of moduleStepLinkOptions(item); track option.id) {
                            <li role="presentation">
                              <button
                                type="button"
                                class="med__tdb-linking-option"
                                role="option"
                                [attr.aria-selected]="isSheetLinkedToModuleStep(item, option)"
                                [disabled]="isSheetLinkedToModuleStep(item, option)"
                                (mousedown)="$event.preventDefault()"
                                (click)="moduleStepLinkSheet(item, option)"
                              >
                                <code>{{ option.key }}</code>
                                <span>{{ option.name }}</span>
                                <span class="med__tdb-linking-option-rows">
                                  {{
                                    'modules.drawer.tdbLinking.rowCount' | translate: { count: option.zeilen.length }
                                  }}
                                </span>
                              </button>
                            </li>
                          }
                        </ul>
                      } @else if (
                        moduleStepLinkOpenFor(item) &&
                        moduleStepLinkSearchFor(item) &&
                        moduleStepLinkOptions(item).length === 0
                      ) {
                        <p class="med__tdb-linking-empty-search">
                          {{ 'modules.drawer.tdbLinking.noMatch' | translate }}
                        </p>
                      }
                    </div>
                  </div>
                </div>
              } @else {
                <div class="med__step">
                  <input
                    class="med__step-title"
                    type="text"
                    [value]="item.title ?? ''"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    (input)="onStepFieldInput(i, 'title', $event)"
                    [attr.aria-label]="'modules.drawer.stepTitle' | translate"
                    [placeholder]="'modules.drawer.stepTitlePlaceholder' | translate"
                  />
                  <textarea
                    class="med__step-field"
                    rows="2"
                    [value]="item.action ?? ''"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    (input)="onStepFieldInput(i, 'action', $event)"
                    [attr.aria-label]="'modules.drawer.stepAction' | translate"
                    [placeholder]="'modules.drawer.stepActionPlaceholder' | translate"
                  ></textarea>
                  <textarea
                    class="med__step-field"
                    rows="2"
                    [value]="item.expectedResult ?? ''"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    (input)="onStepFieldInput(i, 'expectedResult', $event)"
                    [attr.aria-label]="'modules.drawer.stepExpected' | translate"
                    [placeholder]="'modules.drawer.stepExpectedPlaceholder' | translate"
                  ></textarea>
                  <div class="med__step-controls">
                    <select
                      class="med__step-status"
                      (change)="onStepStatusChange(i, $event)"
                      [attr.aria-label]="'modules.drawer.stepStatus' | translate"
                    >
                      @for (s of statusOptions; track s) {
                        <option [value]="s" [selected]="s === statusFor(item)">
                          {{ 'testCases.form.testSteps.statusOptions.' + s | translate }}
                        </option>
                      }
                    </select>
                    <input
                      class="med__step-link"
                      type="text"
                      [value]="item.notes ?? ''"
                      [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                      (input)="onStepFieldInput(i, 'notes', $event)"
                      [attr.aria-label]="'modules.drawer.stepLink' | translate"
                      [placeholder]="'modules.drawer.stepLinkPlaceholder' | translate"
                    />
                  </div>
                </div>
              }
              <div class="med__item-actions">
                <button
                  type="button"
                  class="med__icon-btn"
                  [attr.aria-label]="'modules.drawer.moveUp' | translate"
                  [disabled]="i === 0"
                  (click)="moveItem(i, -1)"
                >
                  <mat-icon aria-hidden="true">keyboard_arrow_up</mat-icon>
                </button>
                <button
                  type="button"
                  class="med__icon-btn"
                  [attr.aria-label]="'modules.drawer.moveDown' | translate"
                  [disabled]="i === steps().length - 1"
                  (click)="moveItem(i, 1)"
                >
                  <mat-icon aria-hidden="true">keyboard_arrow_down</mat-icon>
                </button>
                <button
                  type="button"
                  class="med__icon-btn med__icon-btn--delete"
                  [attr.aria-label]="'modules.drawer.removeItem' | translate"
                  (click)="removeItem(i)"
                >
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
              </div>
            </div>
          }
        </div>

        <div class="med__add-row">
          <button type="button" class="med__add-btn" (click)="addStep()">
            <mat-icon aria-hidden="true">add</mat-icon>
            {{ 'modules.drawer.addStep' | translate }}
          </button>
          <button
            type="button"
            class="med__add-btn"
            [class.med__add-btn--active]="embedPanelOpen()"
            [attr.aria-expanded]="embedPanelOpen()"
            (click)="toggleEmbedPanel()"
          >
            <mat-icon aria-hidden="true">extension</mat-icon>
            {{ 'modules.drawer.embedModule' | translate }}
          </button>
        </div>

        @if (embedPanelOpen()) {
          <app-module-embed-inline
            [projectId]="projectId()"
            [excludeIds]="embedExcludeIds()"
            (applied)="onEmbedApplied($event)"
            (closed)="closeEmbedPanel()"
          ></app-module-embed-inline>
        }

        @if (saveCycleSelf()) {
          <p class="med__error" role="alert">{{ 'modules.drawer.saveCycleSelfError' | translate }}</p>
        } @else if (saveCycleTitle()) {
          <p class="med__error" role="alert">
            {{ 'modules.drawer.saveCycleError' | translate: { title: saveCycleTitle() } }}
          </p>
        } @else if (saveError()) {
          <p class="med__error" role="alert">{{ 'modules.drawer.saveError' | translate }}</p>
        }

        <div class="med__hint">
          <mat-icon aria-hidden="true">check_circle</mat-icon>
          {{ 'modules.drawer.saveHint' | translate }}
        </div>
      }
    </div>

    @if (showTdbApplyBar()) {
      <div class="med__tdb-apply-bar" data-testid="module-drawer-tdb-apply-bar">
        <span class="med__tdb-apply-title">{{ 'modules.drawer.tdb.title' | translate }}</span>
        @if (tdbDraftIncomplete()) {
          <p id="med-tdb-incomplete" class="med__save-blocked" data-testid="module-drawer-tdb-incomplete">
            <mat-icon class="med__save-blocked-icon" aria-hidden="true">info</mat-icon>
            <span>{{ 'modules.drawer.tdb.incomplete' | translate }}</span>
          </p>
        }
        <button
          type="button"
          class="med__btn med__btn--secondary"
          data-testid="module-drawer-tdb-cancel"
          (click)="cancelTdbDraft()"
        >
          {{ 'modules.drawer.tdb.cancel' | translate }}
        </button>
        <button
          type="button"
          class="med__btn med__btn--primary"
          data-testid="module-drawer-tdb-apply"
          [attr.aria-describedby]="tdbDraftIncomplete() ? 'med-tdb-incomplete' : null"
          (click)="applyTdbDraft()"
        >
          {{ 'modules.drawer.tdb.apply' | translate }}
        </button>
      </div>
    }

    <footer class="med__footer">
      @let incompleteSteps = incompleteConditionSteps();
      @if (incompleteSteps.length) {
        <p id="med-save-blocked-condition" class="med__save-blocked" data-testid="module-drawer-save-blocked-condition">
          <mat-icon class="med__save-blocked-icon" aria-hidden="true">info</mat-icon>
          <span>{{ 'modules.drawer.saveBlockedCondition' | translate: { steps: incompleteSteps.join(', ') } }}</span>
        </p>
      }
      <button #cancelButton type="button" class="med__btn med__btn--secondary" (click)="requestClose()">
        {{ 'modules.drawer.cancel' | translate }}
      </button>
      <button
        #saveButton
        type="button"
        class="med__btn med__btn--primary"
        [disabled]="busy() || loading() || authorControl.invalid || incompleteSteps.length > 0"
        [attr.aria-describedby]="incompleteSteps.length ? 'med-save-blocked-condition' : null"
        (click)="save()"
      >
        <mat-icon aria-hidden="true">check</mat-icon>
        {{ 'modules.drawer.save' | translate }}
      </button>
    </footer>
  </div>

  @if (discardTarget()) {
    <div
      class="med__confirm-scrim"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="'modules.drawer.discardTitle' | translate"
    >
      <div class="med__confirm">
        <div class="med__confirm-title">{{ 'modules.drawer.discardTitle' | translate }}</div>
        <p class="med__confirm-text">
          {{
            (moduleDirty() || outerModuleDirty() ? 'modules.drawer.discardText' : 'modules.drawer.discardTextTestdata')
              | translate
          }}
        </p>
        <div class="med__confirm-actions">
          <button type="button" class="med__btn med__btn--secondary" (click)="cancelDiscard()">
            {{ 'modules.drawer.keepEditing' | translate }}
          </button>
          <button type="button" class="med__btn med__btn--danger" (click)="confirmDiscard()">
            {{ 'modules.drawer.discard' | translate }}
          </button>
          <button
            type="button"
            class="med__btn med__btn--primary"
            [disabled]="busy() || authorControl.invalid || incompleteConditionSteps().length > 0"
            (click)="saveFromConfirm()"
          >
            {{ 'modules.drawer.save' | translate }}
          </button>
        </div>
      </div>
    </div>
  }

  <app-multi-use-confirm-dialog
    [(visible)]="multiUseOpen"
    [usageCount]="meta()?.usageCount ?? 0"
    [moduleTitle]="title()"
    (confirmed)="onMultiUseConfirmed()"
  ></app-multi-use-confirm-dialog>
}
